import alertDialogService from "AlertDialogService";
import captionService from "CaptionService";
import type { EntitySaveResult } from "EntitySaveService";
import { FormFlowError } from "FormFlowError";
import type { FormFlowUI } from "FormFlowUITypes";
import { NotificationType } from "NotificationType";
import type { Alert } from "Notifications";
import { registerEntityUsageAsync } from "./FormFlowUIEntityLoader.ts";
import { runExclusiveAsync } from "./FormFlowUIExclusiveActions.ts";
import { handlePendingActionsAsync } from "./FormFlowUIPendingChangesHandler.ts";
import {
  getBoundItemsExcludeDetachedAndDeletedAsync,
  hasClientSideAlert,
  isControlError,
  isRuleErrorOrWarning,
  showValidationResultsAsync,
  validateFormAsync,
} from "./FormFlowUIValidator.ts";

export async function handleSaveChangesAsync(
  formFlowUI: FormFlowUI,
  disableReloadOnSaved: boolean,
): Promise<EntitySaveResult> {
  return await runExclusiveAsync(formFlowUI, async () => {
    const shouldSave = await handlePendingActionsAsync(formFlowUI);
    if (shouldSave) {
      return await saveChangesCoreAsync(formFlowUI, disableReloadOnSaved);
    }
    return { isSaved: false, error: null };
  });
}

export async function ensureSavedAsync(formFlowUI: FormFlowUI, shouldRunExclusive?: boolean): Promise<boolean> {
  if (!shouldRunExclusive) {
    formFlowUI.isBusy(false);
  }

  let isSaved = false;
  await runExclusiveWithSaveAsync(formFlowUI, () => (isSaved = true));
  return isSaved;
}

export async function runExclusiveWithSaveAsync<T>(
  formFlowUI: FormFlowUI,
  callback: () => T,
): Promise<Awaited<T> | undefined> {
  return await runExclusiveAsync(formFlowUI, async () => {
    const isHandled = await handlePendingActionsAsync(formFlowUI);
    let isSaved;
    if (isHandled) {
      const cannotPerformActionTitle = captionService.getString(
        "43e6e521-f29a-4aa0-88a2-8b029745839a",
        "Cannot Perform Action",
      );

      const entity = formFlowUI.entity?.();
      if (!entity) {
        isSaved = false;
      }

      if (!entity?.entityAspect.entityManager.hasChanges()) {
        isSaved = true;
      }
      if (!isSaved) {
        const shouldSave = await alertDialogService.confirmOkCancelAsync(
          captionService.getString(
            "2636df45-0e7f-4ea5-98b8-4bcee598de94",
            "This action cannot be performed right now because there are unsaved changes. Would you like to save?",
          ),
          cannotPerformActionTitle,
          captionService.getString("453aadd1-908c-4e73-8a4f-b3afa6fd16b4", "Save & Continue"),
          undefined,
          NotificationType.Warning,
        );
        if (shouldSave) {
          const saveResult = await saveChangesCoreAsync(formFlowUI, false);
          isSaved = saveResult.isSaved;
        }
      }
    }
    if (isSaved) {
      return callback();
    }
    return;
  });
}

async function saveChangesCoreAsync(formFlowUI: FormFlowUI, disableReloadOnSaved?: boolean): Promise<EntitySaveResult> {
  const isValid = await validateFormAsync(formFlowUI);

  const items = await getBoundItemsExcludeDetachedAndDeletedAsync(formFlowUI);
  const shouldPreventSave = (alert: Alert): boolean => {
    return isControlError(alert) || isRuleErrorOrWarning(alert);
  };
  if (!isValid || hasClientSideAlert(shouldPreventSave, items)) {
    await showValidationResultsAsync(formFlowUI, shouldPreventSave);
    return { isSaved: false, error: null };
  }
  let result;
  try {
    result = await formFlowUI.saveChangesAsync(!disableReloadOnSaved);
  } catch (error) {
    if (error instanceof FormFlowError) {
      formFlowUI.ensureNotAborted();
      formFlowUI.formAwaiter.reject(error);
      return { isSaved: false, error: null };
    } else {
      throw error;
    }
  }
  if (!result) {
    result = { isSaved: false, error: null };
  }
  // Always reload, except where the save was successful and reload on save is disabled for this attempt.
  // So, don't reload on a successful save when saving and closing.
  if (!result.isSaved || !disableReloadOnSaved) {
    try {
      await formFlowUI.reloadEntityAsync();
      await registerEntityUsageAsync(formFlowUI);
    } catch (error) {
      formFlowUI.ensureNotAborted();
      formFlowUI.formAwaiter.reject(error);
    }
  }

  return result;
}
